import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

// ============ GENERIC HELPERS ============

const getAll = async (collectionName, constraints = []) => {
  const q = query(collection(db, collectionName), ...constraints);
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

const getById = async (collectionName, id) => {
  const snap = await getDoc(doc(db, collectionName, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

const create = async (collectionName, data) => {
  const ref = await addDoc(collection(db, collectionName), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: ref.id, ...data };
};

const update = async (collectionName, id, data) => {
  await updateDoc(doc(db, collectionName, id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
  return { id, ...data };
};

const remove = async (collectionName, id) => {
  await deleteDoc(doc(db, collectionName, id));
};

// ============ PROPERTIES ============

export const propertyService = {
  getAll: (userId) =>
    getAll("properties", [where("userId", "==", userId), orderBy("createdAt", "desc")]),
  getById: (id) => getById("properties", id),
  create: (data, userId) => create("properties", { ...data, userId }),
  update: (id, data) => update("properties", id, data),
  delete: (id) => remove("properties", id),
};

// ============ TENANTS ============

export const tenantService = {
  getAll: (userId) =>
    getAll("tenants", [where("userId", "==", userId), orderBy("createdAt", "desc")]),
  getById: (id) => getById("tenants", id),
  create: (data, userId) => create("tenants", { ...data, userId }),
  update: (id, data) => update("tenants", id, data),
  delete: (id) => remove("tenants", id),
};

// ============ LEASES ============

export const leaseService = {
  getAll: (userId) =>
    getAll("leases", [where("userId", "==", userId), orderBy("createdAt", "desc")]),
  getById: (id) => getById("leases", id),
  getByProperty: (propertyId) =>
    getAll("leases", [where("propertyId", "==", propertyId)]),
  getByTenant: (tenantId) =>
    getAll("leases", [where("tenantId", "==", tenantId)]),
  create: (data, userId) => create("leases", { ...data, userId }),
  update: (id, data) => update("leases", id, data),
  delete: (id) => remove("leases", id),
};

// ============ PAYMENTS ============

export const paymentService = {
  getAll: (userId) =>
    getAll("payments", [where("userId", "==", userId), orderBy("createdAt", "desc")]),
  getById: (id) => getById("payments", id),
  getByLease: (leaseId) =>
    getAll("payments", [where("leaseId", "==", leaseId)]),
  getOutstanding: (userId) =>
    getAll("payments", [
      where("userId", "==", userId),
      where("status", "==", "pending"),
    ]),
  create: (data, userId) => create("payments", { ...data, userId }),
  update: (id, data) => update("payments", id, data),
  delete: (id) => remove("payments", id),
};

// ============ MAINTENANCE ============

export const maintenanceService = {
  getAll: (userId) =>
    getAll("maintenance", [where("userId", "==", userId), orderBy("createdAt", "desc")]),
  getById: (id) => getById("maintenance", id),
  getByProperty: (propertyId) =>
    getAll("maintenance", [where("propertyId", "==", propertyId)]),
  create: (data, userId) => create("maintenance", { ...data, userId }),
  update: (id, data) => update("maintenance", id, data),
  delete: (id) => remove("maintenance", id),
};

// ============ EXPENSES ============

export const expenseService = {
  getAll: (userId) =>
    getAll("expenses", [where("userId", "==", userId), orderBy("createdAt", "desc")]),
  getById: (id) => getById("expenses", id),
  getByProperty: (propertyId) =>
    getAll("expenses", [where("propertyId", "==", propertyId)]),
  create: (data, userId) => create("expenses", { ...data, userId }),
  update: (id, data) => update("expenses", id, data),
  delete: (id) => remove("expenses", id),
};

// ============ VIOLATIONS (NYC) ============

export const violationService = {
  getAll: (userId) =>
    getAll("violations", [where("userId", "==", userId), orderBy("createdAt", "desc")]),
  getByProperty: (propertyId) =>
    getAll("violations", [where("propertyId", "==", propertyId)]),
  create: (data, userId) => create("violations", { ...data, userId }),
  update: (id, data) => update("violations", id, data),
};

// ============ VENDORS ============

export const vendorService = {
  getAll: (userId) =>
    getAll("vendors", [where("userId", "==", userId), orderBy("createdAt", "desc")]),
  getById: (id) => getById("vendors", id),
  create: (data, userId) => create("vendors", { ...data, userId }),
  update: (id, data) => update("vendors", id, data),
  delete: (id) => remove("vendors", id),
};

// ============ CONTACTS / CRM ============

export const contactService = {
  getAll: (userId) =>
    getAll("contacts", [where("userId", "==", userId), orderBy("createdAt", "desc")]),
  getById: (id) => getById("contacts", id),
  create: (data, userId) => create("contacts", { ...data, userId }),
  update: (id, data) => update("contacts", id, data),
  delete: (id) => remove("contacts", id),
};

// ============ WORK ORDERS ============

export const workOrderService = {
  getAll: (userId) =>
    getAll("workOrders", [where("userId", "==", userId), orderBy("createdAt", "desc")]),
  getById: (id) => getById("workOrders", id),
  getByVendor: (vendorId) =>
    getAll("workOrders", [where("vendorId", "==", vendorId)]),
  create: (data, userId) => create("workOrders", { ...data, userId }),
  update: (id, data) => update("workOrders", id, data),
  delete: (id) => remove("workOrders", id),
};

// ============ COMMISSIONS ============

export const commissionService = {
  getAll: (userId) =>
    getAll("commissions", [where("userId", "==", userId), orderBy("createdAt", "desc")]),
  getById: (id) => getById("commissions", id),
  create: (data, userId) => create("commissions", { ...data, userId }),
  update: (id, data) => update("commissions", id, data),
  delete: (id) => remove("commissions", id),
};

// ============ COMMISSION RULES ============

export const commissionRuleService = {
  getAll: (userId) =>
    getAll("commissionRules", [where("userId", "==", userId), orderBy("createdAt", "desc")]),
  getById: (id) => getById("commissionRules", id),
  create: (data, userId) => create("commissionRules", { ...data, userId }),
  update: (id, data) => update("commissionRules", id, data),
  delete: (id) => remove("commissionRules", id),
};
